import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import {
  getIntersectionRoot,
  getScrollContainer,
  getScrollTop,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Set<number>} */
  #warmedPages = new Set();

  /** @type {Map<string, Promise<string>>} */
  #pageFetches = new Map();

  #pageGeneration = 0;

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#publishCardBlockSize();
    this.#prefetchAhead('next');
    this.#prefetchAhead('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);

    // Crossing the 990px breakpoint swaps which element actually scrolls, so the observer's root is
    // no longer valid. Rebuild it rather than leaving it bound to the wrong scroller.
    scrollContainerMediaQuery.addEventListener('change', this.#rebuildScrollObserver);
  }

  /** Tear down and re-create the infinite-scroll observer against the current scroll root. */
  #rebuildScrollObserver = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#observeViewMore();
  };

  /** Guards against overlapping renders. The observer callback cannot await, so without this two
   *  fires could both run #renderNextPage and clobber each other's #resolveNextPagePromise. */
  #rendering = { next: false, previous: false };

  /**
   * How far ahead of the visible scroll root the sentinel counts as "in view" (px). Shared by the
   * IntersectionObserver's rootMargin and by #sentinelNeedsMore so the two never disagree.
   */
  static PRELOAD_MARGIN_PX = 3000;

  /** Upper bound on pages appended in one observer fire; a safety stop, never reached in practice. */
  static MAX_PAGES_PER_PASS = 6;

  /**
   * Off-screen cards use `content-visibility: auto`, so an unrendered card is laid out as a placeholder
   * of `--stz-card-block-size`. Publish the real height of an already-rendered card so the placeholder
   * matches and appending/prepending never changes scrollHeight by a guessed amount.
   */
  #publishCardBlockSize() {
    const { grid } = this.refs;
    const card = grid?.querySelector(':scope > [ref="cards[]"]');
    if (!grid || !card) return;
    const height = card.getBoundingClientRect().height;
    if (height > 0) grid.style.setProperty('--stz-card-block-size', `${Math.round(height)}px`);
  }

  /**
   * Is the sentinel still inside the preload margin after an append? IntersectionObserver only
   * reports CHANGES, and one fire can only ever hand us one page, so a flick that covers several
   * pages per second used to wait a full observer round-trip (fire → append → yield → rearm → fire)
   * for every page — a hard stop at the grid's end until the next one landed. Measuring the geometry
   * directly lets #renderNextPage keep appending cached pages in the same pass.
   *
   * @param {"previous" | "next"} type
   * @returns {boolean}
   */
  #sentinelNeedsMore(type) {
    const sentinel = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    if (!sentinel) return false;
    const root = getIntersectionRoot();
    const rootRect = root ? root.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const rect = sentinel.getBoundingClientRect();
    const margin = PaginatedList.PRELOAD_MARGIN_PX;
    return type === 'next' ? rect.top <= rootRect.bottom + margin : rect.bottom >= rootRect.top - margin;
  }

  /**
   * Re-arm the sentinel so the observer reports its CURRENT state.
   *
   * IntersectionObserver only invokes its callback when intersection *changes*. Our preload margin
   * (3000px) is deliberately larger than one page of products (~2800px), so after appending a page
   * the sentinel is usually STILL inside the margin — no change, no callback, and loading silently
   * stops dead until some unrelated event nudges it. Measured: the grid halted at 120 of 251
   * products and only resumed ~3s later, then halted again at 144.
   *
   * Re-observing forces a fresh callback with the current geometry, so if we still need more we keep
   * going. This is what makes the feed actually continuous rather than one-page-at-a-time.
   *
   * @param {"previous" | "next"} type
   */
  #rearmSentinel(type) {
    const sentinel = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    if (!sentinel || !this.infinityScrollObserver) return;
    this.infinityScrollObserver.unobserve(sentinel);
    this.infinityScrollObserver.observe(sentinel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    scrollContainerMediaQuery.removeEventListener('change', this.#rebuildScrollObserver);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // THE ROOT MATTERS. On desktop (>=990px) this theme does not scroll the document — it
          // scrolls `.page-wrapper`, which has overflow-y:auto (see scroll-container.js). Leaving
          // `root` unset makes the observer measure against the document viewport, so the sentinel
          // sits inside a clipping scroll container and the rootMargin below never buys the lead it
          // claims to. That is why the grid kept running out of content on desktop no matter how
          // large the margin got, leaving a half-empty final row on screen.
          //
          // The theme already exports getIntersectionRoot() for exactly this; header.js uses it and
          // this file never did. It returns .page-wrapper on desktop and null (viewport) on mobile,
          // where the document really is the scroller.
          root: getIntersectionRoot(),
          // Preload the next page well before the shopper reaches the end of the loaded grid. This
          // margin is deliberately larger than one 24-product page so a fast flick never catches
          // the sentinel while the next section request is still in flight.
          rootMargin: `${PaginatedList.PRELOAD_MARGIN_PX}px 0px`,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * How many pages to keep warm ahead of the shopper.
   *
   * One page of lead is not enough. A page is ~2800px tall; a fast flick covers that in a frame or
   * two, so the grid repeatedly ran out of loaded content and the scroll simply STOPPED at the
   * bottom while the next request was in flight — measured stalling for 10+ consecutive frames on
   * /collections/all. That dead stop is what reads as "the store ends here", which is the opposite
   * of the endless feel we want. Three pages ≈ 8400px of runway, which outruns a hard flick.
   *
   * Cost is bounded: the section documents are ~25 KB each and cached, but only the immediate next
   * page's visible heroes are warmed. That keeps image traffic from starving the HTML that gates
   * append.
   *
   * Five, not three (2026-09-14): at 24 000 px/s on a 10 Mbps link the pages +2/+3 HTML requests were
   * issued while the previous page's 24 heroes were still downloading and took 1.6–2.6 s to land,
   * so the grid ran dry for ~1 s at page 5. Two more pages of HTML (~50 KB) buy that second back.
   */
  static PREFETCH_AHEAD = 5;

  /**
   * Hero warming shares the link with the section HTML that gates the next append. 24 parallel image
   * streams gave that HTML 1/9 of the bandwidth under HTTP/2 fair sharing; six in flight keeps the
   * warm useful while the HTML still lands first.
   */
  static WARM_CONCURRENCY = 6;

  /** Cards appended per yield so one page never lands as a single long frame (measured 100–380 ms on a phone). */
  static APPEND_CHUNK = 6;

  /** @type {Array<() => void>} */
  #warmQueue = [];

  #warmInFlight = 0;

  /**
   * Keep several pages warm ahead of the shopper, not just the immediate next one.
   * @param {"previous" | "next"} type
   */
  async #prefetchAhead(type) {
    const first = this.#getPage(type);
    if (!first) return;

    const step = type === 'next' ? 1 : -1;
    const pageRequests = [];
    let offset = 0;
    while (pageRequests.length < PaginatedList.PREFETCH_AHEAD) {
      const pageNumber = first.page + step * offset;
      offset += 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber.toString());
      url.hash = '';

      if (!this.#shouldUsePage({ page: pageNumber, url })) break;
      if (this.pages.has(pageNumber)) continue;
      if (this.#pageFetches.has(url.toString())) continue;
      pageRequests.push(
        this.#fetchSpecificPage(pageNumber, url, {
          priority: 'high',
          warmImages: pageNumber === first.page,
        })
      );
    }

    // Fetch the lightweight section documents together, before any image warming can compete with
    // them. Pages +2/+3 stay HTML-only; only the page about to mount gets its heroes warmed.
    await Promise.all(pageRequests);
    this.#warmPage(first.page);
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url, { priority: 'high' });
    this.#warmPage(page.page);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {{ priority?: 'high' | 'low' | 'auto', warmImages?: boolean }} [fetchOptions] - Fetch behavior
   */
  async #fetchSpecificPage(pageNumber, url = undefined, fetchOptions = {}) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) {
      if (fetchOptions.warmImages) this.#warmPage(pageNumber);
      return this.pages.get(pageNumber);
    }

    const pageKey = pageInfo.url.toString();
    const generation = this.#pageGeneration;
    let pageRequest = this.#pageFetches.get(pageKey);
    const ownsRequest = !pageRequest;
    if (!pageRequest) {
      pageRequest = sectionRenderer.getSectionHTML(
        this.sectionId,
        true,
        pageInfo.url,
        undefined,
        fetchOptions
      );
      this.#pageFetches.set(pageKey, pageRequest);
    }

    let pageContent;
    try {
      pageContent = await pageRequest;
      if (generation === this.#pageGeneration) this.pages.set(pageNumber, pageContent);
    } finally {
      if (ownsRequest && this.#pageFetches.get(pageKey) === pageRequest) this.#pageFetches.delete(pageKey);
    }

    if (fetchOptions.warmImages) this.#warmPage(pageNumber);
    return pageContent;
  }

  /** Warm each immediate page at most once. */
  #warmPage(pageNumber) {
    if (this.#warmedPages.has(pageNumber)) return;
    const pageContent = this.pages.get(pageNumber);
    if (!pageContent) return;
    this.#warmedPages.add(pageNumber);
    this.#warmHeroImages(pageContent);
  }

  /**
   * Warm a prefetched page's hero images into the HTTP cache BEFORE its cards ever mount.
   *
   * This is the actual fix for "the bottom rows are blank when I scroll fast". Prefetching only
   * ever warmed the section *HTML*; the images inside it stayed cold until the cards were appended
   * to the DOM, at which point 24 image requests fired at once and the shopper watched them fill in.
   * Measured on /collections/all at 4 Mbps: 12 of 12 visible cards blank for several frames after a
   * page mounted.
   *
   * Warming here means the mount hits a primed cache and paints immediately. Responsive model
   * images use `sizes="auto"` when mounted, so the off-DOM warmer resolves that to the width of an
   * existing card and requests the same candidate the mounted image will use.
   *
   * @param {string} pageHTML
   */
  #warmHeroImages(pageHTML) {
    try {
      const doc = new DOMParser().parseFromString(pageHTML, 'text/html');
      // Only the visible hero of each card. The hover/variant slides are never shown in the grid,
      // and warming them would compete for bandwidth with the images we actually need painted.
      const heroes = doc.querySelectorAll(
        '[ref="cards[]"] slideshow-slide[aria-hidden="false"] img, ' +
          '[ref="cards[]"] .stz-preview-model-media__card-primary img'
      );
      const mountedFrame = this.refs.grid
        ?.querySelector('[ref="cards[]"] .card-gallery')
        ?.getBoundingClientRect();

      for (const img of heroes) {
        // Mirror srcset/sizes so the browser resolves the SAME candidate it will need at mount and
        // the cache actually hits. Setting only `src` warms the wrong (fallback) URL.
        const srcset = img.getAttribute('srcset');
        const sizes = img.getAttribute('sizes');
        const src = img.getAttribute('src');
        const warmSizes = sizes && sizes.trimStart().startsWith('auto') && mountedFrame?.width
          ? `${Math.ceil(this.#paintedWidth(img, mountedFrame))}px`
          : sizes;
        this.#warmQueue.push(() => {
          const warm = new Image();
          warm.fetchPriority = 'low';
          const done = () => {
            this.#warmInFlight -= 1;
            this.#pumpWarmQueue();
          };
          warm.addEventListener('load', done, { once: true });
          warm.addEventListener('error', done, { once: true });
          if (warmSizes) warm.sizes = warmSizes;
          if (srcset) warm.srcset = srcset;
          if (src) warm.src = src;
        });
      }
      this.#pumpWarmQueue();
    } catch {
      // Warming is best-effort. A parse failure must never break pagination itself.
    }
  }

  /** Start queued warms until WARM_CONCURRENCY are in flight. */
  #pumpWarmQueue() {
    while (this.#warmInFlight < PaginatedList.WARM_CONCURRENCY && this.#warmQueue.length) {
      const start = this.#warmQueue.shift();
      this.#warmInFlight += 1;
      start?.();
    }
  }

  /**
   * The width a card hero will actually be PAINTED at, which is what srcset selection must match.
   *
   * The card frame is 2:3 and the image covers it anchored top. A source wider than 2:3 (the retained
   * 2880×2880 squares) is scaled by HEIGHT, so it paints `frameHeight × (w/h)` wide — 1.5× the frame
   * width — while a naive "frame width" guess would warm a candidate the browser never uses. The card
   * CSS sizes the mounted img by the same rule, so `sizes="auto"` resolves to this exact number.
   *
   * @param {Element} img
   * @param {DOMRect} frame
   * @returns {number}
   */
  #paintedWidth(img, frame) {
    const w = Number(img.getAttribute('width'));
    const h = Number(img.getAttribute('height'));
    if (!(w > 0 && h > 0 && frame.height > 0)) return frame.width;
    return Math.max(frame.width, (frame.height * w) / h);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#rendering.next) return;   // an append is already in flight; do not race it
    this.#rendering.next = true;
    try {
      // Keep appending while the sentinel is still inside the preload margin. A hard flick covers
      // several pages per second; one page per observer fire cannot keep up (measured: the grid
      // ran dry for ~0.8 s at 24 000 px/s even with three pages already cached in memory).
      for (let pass = 0; pass < PaginatedList.MAX_PAGES_PER_PASS; pass++) {
        const nextPage = this.#getPage('next');

        if (!nextPage || !this.#shouldUsePage(nextPage)) return;
        let nextPageItemElements = this.#getGridForPage(nextPage.page);

        if (!nextPageItemElements) {
          const promise = new Promise((res) => {
            this.#resolveNextPagePromise = res;
          });

          // Trigger the fetch for this page
          this.#fetchPage('next');

          await promise;
          nextPageItemElements = this.#getGridForPage(nextPage.page);
          if (!nextPageItemElements) return;
        }

        // No image fiddling here: hero bytes were warmed into cache by #warmHeroImages when this page
        // was prefetched.
        if (grid.querySelector(`:scope > [data-page="${nextPage.page}"]`)) return;
        // A few cards per frame: element upgrades and style for 24 cards at once were one 100–380 ms
        // frame on a phone, which reads as a stutter mid-flick.
        const items = [...nextPageItemElements];
        for (let i = 0; i < items.length; i += PaginatedList.APPEND_CHUNK) {
          grid.append(...items.slice(i, i + PaginatedList.APPEND_CHUNK));
          await yieldToMainThread();
        }

        this.#aspectRatioHelper?.processNewElements();

        history.pushState('', '', nextPage.url.toString());

        this.#prefetchAhead('next');

        if (!this.#sentinelNeedsMore('next')) return;
      }
    } finally {
      this.#rendering.next = false;
      // Ask the observer for the sentinel's CURRENT state; if we still need more, keep feeding.
      this.#rearmSentinel('next');
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.#rendering.previous) return;
    this.#rendering.previous = true;
    try {

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // --- Prepend + scroll correction, as ONE synchronous block. Nothing may await in here. ---
    //
    // This is the "scrolling back up glitches hard" bug. Prepending a page inserts ~2800px of
    // content ABOVE the shopper, so scrollTop must grow by exactly that much or the viewport lurches.
    // Two things were fighting:
    //
    //   1. The browser's own scroll anchoring (overflow-anchor defaults to `auto`) was also trying
    //      to correct, on its own schedule, on top of our correction. Two independent corrections
    //      against one scrollTop is the visible judder. We opt out below so ours is the only one.
    //   2. The correction was derived from a getBoundingClientRect() of the old first child, which
    //      is a position that only settles after layout. Measuring scrollHeight before/after is
    //      exact and needs no layout assumptions.
    //
    // Measured before this change on /collections/all?page=5: a +2843px lurch on every prepend.
    const container = getScrollContainer();

    // Opt out of native scroll anchoring for the duration of the mutation, then restore. Scoped to
    // the container rather than set in CSS so nothing else on the page loses anchoring.
    const previousAnchor = container instanceof HTMLElement ? container.style.overflowAnchor : '';
    if (container instanceof HTMLElement) container.style.overflowAnchor = 'none';

    const scrollTopBefore = getScrollTop();
    const heightBefore = container.scrollHeight;

    if (grid.querySelector(`:scope > [data-page="${previousPage.page}"]`)) return;
    grid.prepend(...previousPageItemElements);

    // Reading scrollHeight forces synchronous layout, so the delta below is the real, final height
    // of what we just inserted — not a guess taken before images have been laid out.
    const heightAfter = container.scrollHeight;
    const inserted = heightAfter - heightBefore;

    if (inserted > 0) {
      scrollTo({
        top: scrollTopBefore + inserted,
        behavior: 'instant',
      });
    }

    if (container instanceof HTMLElement) container.style.overflowAnchor = previousAnchor;
    // --- end synchronous block ---

    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    this.#prefetchAhead('previous');
    } finally {
      this.#rendering.previous = false;
      this.#rearmSentinel('previous');
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';
    const cardsLive = grid ? grid.querySelectorAll(':scope > [ref="cards[]"]') : [];
    const targetCard = cardsLive[isPrevious ? 0 : cardsLive.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#pageGeneration += 1;
    this.pages.clear();
    this.#warmedPages.clear();
    this.#pageFetches.clear();
    this.#warmQueue.length = 0;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
